import { ArrowRight, ListChecks, X } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface ScorecardGuideCardProps {
  className?: string;
  title?: string;
  description?: string;
  primaryActionLabel?: string;
  primaryActionDisabled?: boolean;
  onPrimaryAction?: () => void;
}

const setupSteps = ["Choose a signal", "Define success", "Run the scorecard"];

const ScorecardGuideCard = ({
  className,
  title = "Design your first evaluator",
  description = "Tell PromptLayer what a good row looks like. Start with an AI judge, code check, or rule — then run the scorecard to see the overall result.",
  primaryActionLabel,
  primaryActionDisabled = false,
  onPrimaryAction,
}: ScorecardGuideCardProps) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const showDismiss = !onPrimaryAction;

  if (isDismissed) {
    return null;
  }

  return (
    <section
      className={cn(
        "overflow-hidden rounded-xl border border-border/70 bg-gradient-to-b from-background to-muted/10 p-3.5 text-sm text-muted-foreground shadow-sm",
        className,
      )}
    >
      <div className="space-y-3">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border/70 bg-background text-muted-foreground shadow-sm">
            <ListChecks className="h-4 w-4" />
          </span>

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 space-y-1">
                <div className="inline-flex rounded-full border border-border/70 bg-background/80 px-2 py-0.5 text-[11px] font-medium leading-4 text-muted-foreground">
                  Setup
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-semibold leading-5 text-foreground">{title}</p>
                  <p className="text-xs leading-5 text-muted-foreground">{description}</p>
                </div>
              </div>

              {showDismiss ? (
                <button
                  type="button"
                  onClick={() => setIsDismissed(true)}
                  aria-label="Dismiss"
                  className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted/30 hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-border/60 bg-background/80 p-2.5">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs font-medium leading-5 text-foreground">Evaluator</div>
              <div className="truncate text-[11px] leading-4 text-muted-foreground">
                Grade every row against one clear rule
              </div>
            </div>
            <div className="rounded-md border border-border/70 bg-muted/20 px-1.5 py-0.5 text-[10px] font-medium leading-4 text-muted-foreground">
              Draft
            </div>
          </div>
          <div className="mt-2 grid gap-1.5">
            {setupSteps.map((step, index) => (
              <div key={step} className="flex items-center gap-2 text-xs leading-5">
                <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-muted/50 text-[10px] font-medium text-muted-foreground">
                  {index + 1}
                </span>
                <span>{step}</span>
              </div>
            ))}
          </div>
        </div>

        {onPrimaryAction ? (
          <Button
            size="sm"
            className="h-8 w-full rounded-lg px-3 text-xs font-medium"
            onClick={onPrimaryAction}
            disabled={primaryActionDisabled}
          >
            {primaryActionLabel ?? "Create evaluator"}
          </Button>
        ) : (
          <div className="flex flex-wrap items-center gap-1.5 text-[11px] leading-5 text-muted-foreground">
            <span className="rounded bg-background px-1.5 py-0.5 font-medium text-foreground">
              Rows
            </span>
            <ArrowRight className="h-3 w-3" />
            <span className="rounded bg-background px-1.5 py-0.5 font-medium text-foreground">
              Evaluators
            </span>
            <ArrowRight className="h-3 w-3" />
            <span className="rounded bg-background px-1.5 py-0.5 font-medium text-foreground">
              Overall score
            </span>
          </div>
        )}
      </div>
    </section>
  );
};

export default ScorecardGuideCard;
